import React, { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { configService } from '../../services/configService';
import { MONTHS } from '../../utils/financialPeriods';
import { isAdmin } from '../../utils/permissions';

const inputCls =
  'w-full px-3 py-2 text-sm border rounded-lg outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 border-gray-200 dark:border-slate-600 focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60';

// Cap the raw file so the base64 stays comfortably under the server's limit
// (md_config.config_value is TEXT; the API rejects logos over ~750 KB).
const MAX_LOGO_BYTES = 500 * 1024;

const ConfigItemsModal = ({ onClose }) => {
  const { config, refreshConfig } = useAppContext();
  const [vatRate, setVatRate] = useState(config.vat_rate ?? '15');
  const [fyStart, setFyStart] = useState(String(config.fy_start_month ?? '1'));
  const [logoData, setLogoData] = useState(config.company_logo ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const admin = isAdmin();

  // Compare against what was loaded, so Save only lights up on a real change.
  const hasChanges =
    String(vatRate) !== String(config.vat_rate ?? '15') ||
    String(fyStart) !== String(config.fy_start_month ?? '1') ||
    (logoData ?? '') !== (config.company_logo ?? '');

  const onLogoFile = (e) => {
    setError('');
    setMsg('');
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      return setError('Please choose an image file (PNG, JPG, SVG, WebP).');
    }
    if (file.size > MAX_LOGO_BYTES) {
      return setError(`Logo is too large (${Math.round(file.size / 1024)} KB). Max 500 KB — please upload a smaller image.`);
    }
    const reader = new FileReader();
    reader.onload = () => setLogoData(String(reader.result || ''));
    reader.onerror = () => setError('Could not read that file. Try another image.');
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setError('');
    setMsg('');
    const rate = Number(vatRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 100) {
      return setError('VAT rate must be a number between 0 and 100.');
    }
    try {
      setBusy(true);
      await configService.saveConfig({
        vat_rate: String(rate),
        fy_start_month: fyStart,
        company_logo: logoData,
      });
      await refreshConfig();
      setMsg('Saved.');
      setTimeout(onClose, 600);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save config.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6 font-sans"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white dark:bg-slate-800 w-full max-w-md rounded-xl shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Config Items</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg leading-none"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
              VAT Rate (%)
            </label>
            <input
              type="number" min="0" max="100" step="0.01"
              value={vatRate}
              disabled={!admin}
              onChange={(e) => { setVatRate(e.target.value); setError(''); }}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-slate-400">
              Applied to the customs value (components up to and including Import Duty) on the Stocked Pricing page.
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
              Financial Year Start Month
            </label>
            <select value={fyStart} disabled={!admin} onChange={(e) => setFyStart(e.target.value)} className={inputCls}>
              {MONTHS.map((m, i) => <option key={i} value={String(i + 1)}>{m}</option>)}
            </select>
            <p className="mt-1 text-xs text-slate-400">
              Financial period 1 maps to this month across the OPEX and Pricing screens.
            </p>
          </div>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
              Company Logo
            </p>
            <div className="flex items-center gap-3">
              <div className="h-14 w-14 shrink-0 rounded-lg border border-slate-200 dark:border-slate-600 bg-white flex items-center justify-center overflow-hidden">
                {logoData
                  ? <img src={logoData} alt="Logo preview" className="max-h-12 max-w-12 object-contain" />
                  : <span className="text-[10px] text-slate-400">No logo</span>}
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  disabled={!admin}
                  onChange={onLogoFile}
                  className="block w-full text-xs text-slate-500 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 dark:file:border-slate-600 file:text-xs file:bg-white dark:file:bg-slate-700 file:text-slate-700 dark:file:text-slate-200 hover:file:bg-slate-50 disabled:opacity-60"
                />
                {logoData && admin && (
                  <button
                    type="button"
                    onClick={() => { setLogoData(''); setError(''); }}
                    className="mt-1.5 text-xs text-red-500 hover:text-red-600"
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-slate-400">
              Shown top-left in the sidebar. Include any company name / tagline in the image itself.
              PNG or SVG on a transparent background works best. Max 500 KB.
            </p>
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
          {msg && <p className="text-sm text-green-600">{msg}</p>}
          {!admin && <p className="text-xs text-amber-600">You need an administrative role to change these.</p>}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !admin || !hasChanges}
            className={`px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 ${
              hasChanges ? 'bg-emerald-600 hover:bg-emerald-700 font-semibold' : 'bg-slate-900 hover:bg-slate-800'
            }`}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigItemsModal;
